<script setup lang="ts">
// Floating overlay that shows every CustomEvent the <cad-sketch> element
// dispatches. Wired from AppView; state lives in src/ui/eventLog.ts. Intended
// for baseline observation, integration demos, and future public-API smoke
// tests — deliberately visible in production too.

import { computed, ref } from 'vue'

import {
  clear,
  setMirror,
  setMode,
  useEventLog,
  type EventLogEntry,
} from '../ui/eventLog'

const { entries, mode, mirror } = useEventLog()

// v-model-friendly wrapper for the readonly `mirror` ref. Keeps the underlying
// state single-writer (via setMirror), while giving the checkbox a real
// two-way binding rather than a fragile :checked + @change pair.
const consoleMirror = computed({
  get: () => mirror.value,
  set: (v) => setMirror(v),
})

// Expanded rows are tracked by entry id so the set survives new entries being
// appended above/below without losing which one the user is inspecting.
const expanded = ref<Set<number>>(new Set())

const orderedEntries = computed(() => entries.value.slice().reverse())

function formatTime(ts: number): string {
  const d  = new Date(ts)
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  const ss = String(d.getSeconds()).padStart(2, '0')
  const ms = String(d.getMilliseconds()).padStart(3, '0')
  return `${hh}:${mm}:${ss}.${ms}`
}

function summarize(detail: unknown): string {
  if (detail == null) return ''
  if (typeof detail !== 'object') return String(detail)
  const parts: string[] = []
  for (const [k, v] of Object.entries(detail as Record<string, unknown>)) {
    if (v == null) { parts.push(`${k}=${v}`); continue }
    if (Array.isArray(v)) { parts.push(`${k}[${v.length}]`); continue }
    if (typeof v === 'object') { parts.push(`${k}={…}`); continue }
    if (typeof v === 'string') { parts.push(`${k}=${v.length > 24 ? v.slice(0, 24) + '…' : v}`); continue }
    parts.push(`${k}=${String(v)}`)
  }
  return parts.join(' ')
}

function formatDetail(detail: unknown): string {
  try {
    return JSON.stringify(detail, jsonReplacer, 2)
  } catch {
    return String(detail)
  }
}

// Sets and Maps stringify to `{}` by default, which is misleading in an
// engine payload (SnapSettings.activeTypes is a Set). Expand them to arrays
// for display only.
function jsonReplacer(_key: string, value: unknown): unknown {
  if (value instanceof Set) return Array.from(value)
  if (value instanceof Map) return Object.fromEntries(value)
  return value
}

function toggleExpand(id: number) {
  const next = new Set(expanded.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  expanded.value = next
}

function eventKindClass(name: string): string {
  if (name.startsWith('sketch-entity-')) return 'evt-entity'
  if (name === 'sketch-document-changed') return 'evt-doc'
  if (name === 'sketch-ready' || name === 'sketch-session-started') return 'evt-lifecycle'
  if (name === 'sketch-error') return 'evt-error'
  if (name === 'sketch-license-validated') return 'evt-license'
  if (name.startsWith('cad-page-') || name === 'sketch-page-changed') return 'evt-page'
  if (name === 'sketch-selection-changed') return 'evt-selection'
  if (name.startsWith('sketch-tool') || name.startsWith('cad-tool')) return 'evt-tool'
  return 'evt-other'
}

function trackId(entry: EventLogEntry): number { return entry.id }
</script>

<template>
  <button
    v-if="mode === 'collapsed'"
    type="button"
    class="event-log-reopen"
    aria-label="Expand event log"
    @click="setMode('visible')"
  >
    Events <span class="pill">{{ entries.length }}</span>
  </button>

  <section
    v-else-if="mode === 'visible'"
    class="event-log"
    role="log"
    aria-live="off"
    aria-label="Sketch event log"
  >
    <header class="event-log__header">
      <span class="event-log__title">Sketch events</span>
      <span class="event-log__count" aria-label="Captured event count">{{ entries.length }}</span>

      <label class="event-log__mirror" title="Also log to browser console">
        <input type="checkbox" v-model="consoleMirror" />
        console
      </label>

      <button type="button" class="event-log__btn" @click="clear">Clear</button>
      <button
        type="button"
        class="event-log__btn event-log__btn--icon"
        aria-label="Collapse event log to pill"
        title="Minimize to pill"
        @click="setMode('collapsed')"
      >&minus;</button>
      <button
        type="button"
        class="event-log__btn event-log__btn--icon"
        aria-label="Close event log"
        title="Close (hide from UI — reopen from account menu)"
        @click="setMode('off')"
      >×</button>
    </header>

    <ol class="event-log__list">
      <li v-if="entries.length === 0" class="event-log__empty">
        No events yet — interact with the canvas.
      </li>

      <li
        v-for="entry in orderedEntries"
        :key="trackId(entry)"
        class="event-log__row"
        :class="{ 'event-log__row--expanded': expanded.has(entry.id) }"
      >
        <button
          type="button"
          class="event-log__row-summary"
          @click="toggleExpand(entry.id)"
        >
          <span class="event-log__time">{{ formatTime(entry.ts) }}</span>
          <span class="event-log__name" :class="eventKindClass(entry.name)">{{ entry.name }}</span>
          <span class="event-log__detail-summary">{{ summarize(entry.detail) }}</span>
        </button>
        <pre v-if="expanded.has(entry.id)" class="event-log__detail-json">{{ formatDetail(entry.detail) }}</pre>
      </li>
    </ol>
  </section>
</template>

<style scoped>
/* View-unique overlay chrome. Positioned relative to the viewport rather
   than a Shell child so it floats over the canvas without affecting layout.
   Uses tokens for colors, spacing, radius, shadow, and z-index. */

.event-log {
  position: fixed;
  right:    var(--sp-3);
  bottom:   var(--sp-3);
  width:    400px;
  height:   300px;
  display:  flex;
  flex-direction: column;
  background: var(--bg);
  color: var(--fg);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  /* Sits above menus but below dialogs, so opening a dialog covers the log
     rather than the log covering a modal. */
  z-index: calc(var(--z-menu) + 10);
  font-family: var(--font);
  font-size: 12px;
  overflow: hidden;
}

.event-log__header {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  padding: var(--sp-2) var(--sp-3);
  border-bottom: 1px solid var(--border);
  background: var(--bg-subtle);
}

.event-log__title {
  font-weight: 600;
  color: var(--fg);
}

.event-log__count {
  color: var(--fg-muted);
  font-variant-numeric: tabular-nums;
  min-width: 2ch;
  text-align: right;
}

.event-log__mirror {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: var(--sp-1);
  color: var(--fg-muted);
  cursor: pointer;
  user-select: none;
}

.event-log__mirror input { margin: 0; }

.event-log__btn {
  appearance: none;
  border: 1px solid var(--border);
  background: var(--bg);
  color: var(--fg);
  padding: 2px var(--sp-2);
  border-radius: 4px;
  font: inherit;
  cursor: pointer;
}

.event-log__btn:hover { background: var(--bg-subtle); }

.event-log__btn--icon {
  padding: 0 6px;
  font-size: 14px;
  line-height: 1;
  min-width: 22px;
}

.event-log__list {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  flex: 1 1 auto;
}

.event-log__empty {
  padding: var(--sp-3);
  color: var(--fg-muted);
  text-align: center;
}

.event-log__row {
  border-bottom: 1px solid var(--border);
}

.event-log__row:last-child { border-bottom: none; }

.event-log__row-summary {
  display: grid;
  grid-template-columns: 92px minmax(auto, 200px) 1fr;
  gap: var(--sp-2);
  width: 100%;
  padding: 4px var(--sp-3);
  background: transparent;
  border: 0;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  align-items: baseline;
}

.event-log__row-summary:hover { background: var(--bg-subtle); }

.event-log__row--expanded .event-log__row-summary { background: var(--bg-subtle); }

.event-log__time {
  color: var(--fg-muted);
  font-family: ui-monospace, SFMono-Regular, 'Cascadia Mono', Consolas, monospace;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.event-log__name {
  font-family: ui-monospace, SFMono-Regular, 'Cascadia Mono', Consolas, monospace;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.event-log__detail-summary {
  color: var(--fg-muted);
  font-family: ui-monospace, SFMono-Regular, 'Cascadia Mono', Consolas, monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.event-log__detail-json {
  margin: 0;
  padding: var(--sp-2) var(--sp-3) var(--sp-3);
  background: var(--bg-subtle);
  color: var(--fg);
  font-family: ui-monospace, SFMono-Regular, 'Cascadia Mono', Consolas, monospace;
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-all;
  overflow-x: auto;
}

/* Event-kind color coding — soft accents, not distracting. */
.evt-doc       { color: var(--accent); }
.evt-entity    { color: #2f855a; }
.evt-lifecycle { color: var(--accent); }
.evt-error     { color: var(--danger); }
.evt-license   { color: var(--warn); }
.evt-page      { color: #805ad5; }
.evt-selection { color: #d69e2e; }
.evt-tool      { color: #dd6b20; }
.evt-other     { color: var(--fg); }

@media (prefers-color-scheme: dark) {
  .evt-entity    { color: #68d391; }
  .evt-page      { color: #b794f4; }
  .evt-selection { color: #ecc94b; }
  .evt-tool      { color: #f6ad55; }
}

.event-log-reopen {
  position: fixed;
  /* Right-aligned but pushed left so the pill doesn't sit over the engine's
     pages tab strip in the bottom-right of the canvas. */
  right:  150px;
  bottom: var(--sp-3);
  z-index: calc(var(--z-menu) + 10);
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  padding: 6px var(--sp-3);
  background: var(--bg);
  color: var(--fg);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  font: inherit;
  font-size: 12px;
  cursor: pointer;
}

.event-log-reopen:hover { background: var(--bg-subtle); }

.event-log-reopen .pill {
  background: var(--accent);
  color: var(--accent-fg);
  border-radius: 999px;
  padding: 0 6px;
  font-variant-numeric: tabular-nums;
  font-size: 11px;
}
</style>
